import { Eye, EyeOff } from 'lucide-react'
import { forwardRef, type InputHTMLAttributes, useState } from 'react'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/**
 * Campo de senha com botão de mostrar/esconder.
 *
 * Mostrar a senha é recurso de acessibilidade, não de conveniência: senha de 12
 * caracteres digitada às cegas em teclado de celular erra muito, e o usuário
 * responde a isso escolhendo uma senha mais curta e mais óbvia. O botão fica
 * FORA do fluxo de tabulação (`tabIndex={-1}`) para não atrapalhar quem navega
 * por teclado entre os campos do formulário.
 */
export type CampoDeSenhaProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>

export const CampoDeSenha = forwardRef<HTMLInputElement, CampoDeSenhaProps>(
  ({ className, ...props }, ref) => {
    const [visivel, setVisivel] = useState(false)

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visivel ? 'text' : 'password'}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            setVisivel((atual) => !atual)
          }}
          aria-label={visivel ? 'Esconder senha' : 'Mostrar senha'}
          aria-pressed={visivel}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-fg-muted',
            'transition-colors hover:text-foreground focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {visivel ? (
            <EyeOff className="size-4" aria-hidden="true" />
          ) : (
            <Eye className="size-4" aria-hidden="true" />
          )}
        </button>
      </div>
    )
  },
)
CampoDeSenha.displayName = 'CampoDeSenha'
